import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Button,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

// ✅ Replace with your real images
// import img1 from "../../assets/help/step1.png";
// import img2 from "../../assets/help/step2.png";
// import img3 from "../../assets/help/step3.png";

export default function EditorHelpModal({ open, onClose }) {
  const slides = useMemo(
    () => [
      {  title: "Typing & formatting", text: "Use the toolbar for bold/italic, font size, colors, and alignment." },
      {  title: "Insert math", text: "Click Insert Math, type LaTeX, and it renders in the editor." },
      {  title: "Export PDF", text: "Use Export PDF to preview pages and download only what you need." },
    ],
    []
  );

  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!open) return;
    setIndex(0); // reset on open
  }, [open]);

  const prev = useCallback(() => setIndex((i) => (i === 0 ? slides.length - 1 : i - 1)), [slides.length]);
  const next = useCallback(() => setIndex((i) => (i === slides.length - 1 ? 0 : i + 1)), [slides.length]);

  // Optional: keyboard arrows
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, prev, next, onClose]);

  const slide = slides[index];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        How to use the editor
        <Box sx={{ flexGrow: 1 }} />
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton onClick={prev} aria-label="Previous">
            <NavigateBeforeIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }}>
            <Box
              sx={{
                width: "100%",
                background: "#f5f5f5",
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid #e0e0e0",
              }}
            >
              <img
                src={slide.img}
                alt={slide.title}
                style={{ width: "100%", display: "block" }}
              />
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="h6">{slide.title}</Typography>
              <Typography variant="body2" color="text.secondary">
                {slide.text}
              </Typography>
            </Box>

            {/* Dots */}
            <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 1 }}>
              {slides.map((_, i) => (
                <Box
                  key={i}
                  onClick={() => setIndex(i)}
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    cursor: "pointer",
                    background: i === index ? "#1976d2" : "#cfcfcf",
                  }}
                />
              ))}
            </Box>
          </Box>

          <IconButton onClick={next} aria-label="Next">
            <NavigateNextIcon />
          </IconButton>
        </Box>

        <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between" }}>
          <Button onClick={prev} startIcon={<NavigateBeforeIcon />}>
            Back
          </Button>
          <Typography variant="caption" color="text.secondary">
            {index + 1} / {slides.length}
          </Typography>
          <Button onClick={next} endIcon={<NavigateNextIcon />}>
            Next
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
